import React from 'react';
import { FileText, Download, Trash2, ShieldCheck, CheckCircle2 } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useAuth } from '../context/AuthContext';

interface Payment {
    id: string;
    amount: number;
    currency: string;
    credits: string;
    date: Date;
    receipt: string;
    razorpay_payment_id?: string;
    expiryDate?: Date;
    planName?: string;
}

interface BillingHistoryProps {
    payments: Payment[];
    onDelete?: (id: string) => void;
}

export function BillingHistory({ payments, onDelete }: BillingHistoryProps) {
    const { user } = useAuth();

    if (!payments || payments.length === 0) {
        return null;
    }

    const downloadInvoice = async (payment: Payment) => {
        const dateObj = new Date(payment.date);
        const formattedDate = dateObj.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });

        const isUnlimited = payment.credits === 'UNLIMITED' || (typeof payment.credits === 'number' && Number(payment.credits) > 900000);
        const planNameFormatted = payment.planName || (isUnlimited ? 'Unlimited API Hits' : `${payment.credits} Credits Plan`);
        const transactionId = payment.razorpay_payment_id || payment.receipt || `TXN_${Date.now()}`;
        const invoiceNumber = `INV-${dateObj.getFullYear()}-${transactionId.slice(-8).toUpperCase()}`;
        const expiryFormatted = payment.expiryDate
            ? new Date(payment.expiryDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
            : 'Lifetime Access';

        // Create a temporary container for the invoice with exact pixel dimensions
        const invoiceContainer = document.createElement('div');
        invoiceContainer.style.position = 'absolute';
        invoiceContainer.style.left = '-9999px';
        invoiceContainer.style.top = '0';
        invoiceContainer.style.width = '794px';
        invoiceContainer.style.backgroundColor = '#ffffff';
        invoiceContainer.style.color = '#0f172a';
        invoiceContainer.style.fontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
        invoiceContainer.style.boxSizing = 'border-box';

        // Proportioned, Cohesive Luxury Invoice HTML Template
        invoiceContainer.innerHTML = `
            <div style="padding: 44px 48px; background: #ffffff; box-sizing: border-box; position: relative;">
                
                <!-- Top Brand Gradient Accent Bar -->
                <div style="position: absolute; top: 0; left: 0; right: 0; height: 5px; background: linear-gradient(90deg, #4f46e5 0%, #3b82f6 50%, #06b6d4 100%);"></div>

                <!-- Header Section -->
                <div style="display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 24px; border-bottom: 1.5px solid #f1f5f9;">
                    <!-- Logo & Brand -->
                    <div style="display: flex; align-items: center; gap: 14px;">
                        <div style="width: 48px; height: 48px; background: #0f172a; border-radius: 12px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 10px rgba(15, 23, 42, 0.12);">
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                            </svg>
                        </div>
                        <div>
                            <div style="display: flex; align-items: center; gap: 6px;">
                                <span style="font-size: 24px; font-weight: 900; color: #0f172a; letter-spacing: -0.8px;">BgRemover</span>
                                <span style="background: linear-gradient(135deg, #4f46e5, #06b6d4); color: #ffffff; font-size: 10px; font-weight: 800; padding: 2px 6px; border-radius: 5px; letter-spacing: 0.5px;">PRO</span>
                            </div>
                            <p style="font-size: 10px; font-weight: 600; color: #64748b; margin: 2px 0 0 0; letter-spacing: 1px; text-transform: uppercase;">Enterprise AI Image Platform</p>
                        </div>
                    </div>

                    <!-- Invoice Title & Status Badge -->
                    <div style="text-align: right;">
                        <div style="font-size: 28px; font-weight: 900; color: #0f172a; letter-spacing: -0.5px; line-height: 1;">TAX INVOICE</div>
                        <div style="margin-top: 4px; font-family: 'SF Mono', Monaco, Consolas, monospace; font-size: 12px; font-weight: 700; color: #64748b;">
                            #${invoiceNumber}
                        </div>
                        <div style="margin-top: 8px; display: inline-flex; align-items: center; gap: 5px; background: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; padding: 3px 10px; border-radius: 9999px; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.6px;">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#047857" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            Payment Completed
                        </div>
                    </div>
                </div>

                <!-- Client & Meta Information Cards -->
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 20px; margin-bottom: 24px;">
                    <!-- Billed To -->
                    <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px 18px;">
                        <span style="font-size: 9px; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.8px; display: block; margin-bottom: 6px;">Billed To</span>
                        <div style="font-size: 15px; font-weight: 800; color: #0f172a; margin-bottom: 3px;">
                            ${user?.displayName || user?.email?.split('@')[0] || 'Valued Customer'}
                        </div>
                        <div style="font-size: 12px; color: #475569; font-weight: 500;">
                            ${user?.email || 'N/A'}
                        </div>
                        <div style="margin-top: 8px; font-size: 10px; color: #64748b; font-family: monospace;">
                            Account ID: ${user?.userId ? user.userId.slice(0, 16) + '...' : 'Direct User'}
                        </div>
                    </div>

                    <!-- Invoice Details -->
                    <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px 18px;">
                        <span style="font-size: 9px; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.8px; display: block; margin-bottom: 6px;">Payment Summary</span>
                        <div style="display: grid; grid-template-columns: auto 1fr; gap: 5px 12px; font-size: 11px;">
                            <span style="color: #64748b; font-weight: 600;">Issue Date:</span>
                            <span style="color: #0f172a; font-weight: 700; text-align: right;">${formattedDate}</span>

                            <span style="color: #64748b; font-weight: 600;">Payment Mode:</span>
                            <span style="color: #0f172a; font-weight: 700; text-align: right;">Razorpay Gateway</span>

                            <span style="color: #64748b; font-weight: 600;">Transaction Ref:</span>
                            <span style="color: #0f172a; font-weight: 700; font-family: monospace; font-size: 10px; text-align: right;">${transactionId}</span>

                            <span style="color: #64748b; font-weight: 600;">Plan Validity:</span>
                            <span style="color: #2563eb; font-weight: 700; text-align: right;">${expiryFormatted}</span>
                        </div>
                    </div>
                </div>

                <!-- Line Items Table -->
                <div style="border: 1.5px solid #e2e8f0; border-radius: 10px; overflow: hidden; margin-bottom: 22px;">
                    <table style="width: 100%; border-collapse: collapse; text-align: left;">
                        <thead>
                            <tr style="background: #f8fafc; border-bottom: 1.5px solid #e2e8f0;">
                                <th style="padding: 12px 18px; font-size: 10px; font-weight: 800; text-transform: uppercase; color: #475569; letter-spacing: 0.8px;">Item Description</th>
                                <th style="padding: 12px 18px; font-size: 10px; font-weight: 800; text-transform: uppercase; color: #475569; letter-spacing: 0.8px; text-align: center; width: 70px;">Qty</th>
                                <th style="padding: 12px 18px; font-size: 10px; font-weight: 800; text-transform: uppercase; color: #475569; letter-spacing: 0.8px; text-align: right; width: 130px;">Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style="padding: 16px 18px;">
                                    <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 5px;">
                                        <span style="font-size: 14px; font-weight: 800; color: #0f172a;">${planNameFormatted}</span>
                                        <span style="background: #eff6ff; color: #2563eb; font-size: 9px; font-weight: 700; padding: 2px 7px; border-radius: 5px; border: 1px solid #bfdbfe;">
                                            ${isUnlimited ? 'UNLIMITED ACCESS' : 'CREDIT PACK'}
                                        </span>
                                    </div>
                                    <div style="font-size: 11px; color: #64748b; line-height: 1.5;">
                                        High-resolution automated background removal processing, ultra-low latency REST API access, custom background replacement, and developer SDK support.
                                    </div>
                                    <div style="margin-top: 6px; font-size: 10px; color: #475569; font-weight: 600;">
                                        • Period: <span style="color: #0f172a;">${formattedDate} — ${expiryFormatted}</span>
                                    </div>
                                </td>
                                <td style="padding: 16px 18px; text-align: center; vertical-align: top; font-size: 13px; font-weight: 700; color: #0f172a;">
                                    1
                                </td>
                                <td style="padding: 16px 18px; text-align: right; vertical-align: top; font-size: 14px; font-weight: 800; color: #0f172a;">
                                    ${payment.currency} ${payment.amount.toFixed(2)}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Calculation & Grand Total Section -->
                <div style="display: flex; justify-content: flex-end; margin-bottom: 24px;">
                    <div style="width: 320px;">
                        <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f1f5f9; font-size: 12px;">
                            <span style="color: #64748b; font-weight: 500;">Subtotal</span>
                            <span style="font-weight: 700; color: #0f172a;">${payment.currency} ${payment.amount.toFixed(2)}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f1f5f9; font-size: 12px;">
                            <span style="color: #64748b; font-weight: 500;">Estimated Tax / GST (0%)</span>
                            <span style="font-weight: 700; color: #0f172a;">${payment.currency} 0.00</span>
                        </div>

                        <!-- Total Paid Highlight Card -->
                        <div style="margin-top: 12px; background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); border-radius: 10px; padding: 14px 18px; color: #ffffff; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 6px 14px rgba(15, 23, 42, 0.12);">
                            <div>
                                <span style="font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: #94a3b8; display: block;">Total Paid</span>
                                <span style="font-size: 9px; color: #cbd5e1;">All taxes included</span>
                            </div>
                            <div style="font-size: 22px; font-weight: 900; color: #38bdf8; letter-spacing: -0.5px;">
                                ${payment.currency} ${payment.amount.toFixed(2)}
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Security Verification Banner -->
                <div style="background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 8px; padding: 12px 16px; margin-bottom: 22px; display: flex; align-items: center; justify-content: space-between;">
                    <div style="display: flex; align-items: center; gap: 8px;">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#047857" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                        </svg>
                        <div>
                            <div style="font-size: 11px; font-weight: 800; color: #0f172a;">Cryptographically Verified Electronic Invoice</div>
                            <div style="font-size: 10px; color: #64748b;">Authenticated via Razorpay Secure Gateway & Firebase Auth Engine</div>
                        </div>
                    </div>
                    <div style="font-size: 10px; font-weight: 700; color: #047857; text-transform: uppercase; letter-spacing: 0.5px;">
                        Status: Settled
                    </div>
                </div>

                <!-- Footer -->
                <div style="padding-top: 18px; border-top: 1.5px solid #e2e8f0; display: flex; justify-content: space-between; align-items: flex-start; font-size: 10px; color: #64748b;">
                    <div>
                        <div style="font-weight: 800; color: #0f172a; text-transform: uppercase; margin-bottom: 3px; font-size: 11px;">
                            BgRemover Technologies
                        </div>
                        <div>Kandivali West, Mumbai 400101, Maharashtra, India</div>
                        <div style="margin-top: 2px;">
                            Email: <span style="color: #0f172a; font-weight: 600;">hello@webappdigitalsolutions.com</span> | Website: <span style="color: #2563eb; font-weight: 600;">bgdrop.cloud</span>
                        </div>
                    </div>
                    <div style="text-align: right; max-width: 220px; line-height: 1.4;">
                        This is a computer-generated tax invoice and requires no physical signature.
                    </div>
                </div>

            </div>
        `;

        document.body.appendChild(invoiceContainer);

        try {
            const canvas = await html2canvas(invoiceContainer, {
                scale: 3, // Ultra-sharp 300 DPI rendering
                logging: false,
                useCORS: true,
                backgroundColor: '#ffffff',
                windowWidth: 794
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const imgWidth = 210; // A4 mm width
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');
            pdf.save(`Invoice_${transactionId}.pdf`);
        } catch (error) {
            console.error('Error generating invoice:', error);
            alert('Failed to generate invoice. Please try again.');
        } finally {
            document.body.removeChild(invoiceContainer);
        }
    };

    return (
        <div className="w-full">
            {/* Desktop Table View (md and up) */}
            <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead>
                        <tr className="border-b border-border text-muted-foreground text-xs uppercase tracking-wider">
                            <th className="pb-3 font-semibold">Date</th>
                            <th className="pb-3 font-semibold">Usage Plan</th>
                            <th className="pb-3 font-semibold">Validity</th>
                            <th className="pb-3 font-semibold">Amount</th>
                            <th className="pb-3 font-semibold text-center">Status</th>
                            <th className="pb-3 font-semibold text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {payments.map((payment) => {
                            const isUnlimited = payment.credits === 'UNLIMITED' || (typeof payment.credits === 'number' && Number(payment.credits) > 900000);
                            const planDisplay = payment.planName || (isUnlimited ? 'Unlimited Hits' : `${payment.credits} Credits`);
                            return (
                                <tr key={payment.id} className="group hover:bg-muted/30 transition-colors">
                                    <td className="py-4 text-muted-foreground font-medium text-xs">
                                        {new Date(payment.date).toLocaleDateString()}
                                    </td>
                                    <td className="py-4 font-bold text-foreground">
                                        {planDisplay}
                                    </td>
                                    <td className="py-4 text-muted-foreground text-xs">
                                        {payment.expiryDate ? new Date(payment.expiryDate).toLocaleDateString() : 'Lifetime'}
                                    </td>
                                    <td className="py-4 text-foreground font-bold font-mono">
                                        {payment.currency} {payment.amount.toFixed(2)}
                                    </td>
                                    <td className="py-4 text-center">
                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20">
                                            <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                            PAID
                                        </span>
                                    </td>
                                    <td className="py-4 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => downloadInvoice(payment)}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground rounded-lg transition-all shadow-sm active:scale-95"
                                                title="Download PDF Invoice"
                                            >
                                                <Download className="h-3.5 w-3.5" />
                                                Download PDF
                                            </button>
                                            {onDelete && (
                                                <button
                                                    onClick={() => {
                                                        if (window.confirm('Are you sure you want to delete this payment record?')) {
                                                            onDelete(payment.id);
                                                        }
                                                    }}
                                                    className="p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                                                    title="Delete Record"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Mobile Card View (< md screens) */}
            <div className="md:hidden space-y-3">
                {payments.map((payment) => {
                    const isUnlimited = payment.credits === 'UNLIMITED' || (typeof payment.credits === 'number' && Number(payment.credits) > 900000);
                    const planDisplay = payment.planName || (isUnlimited ? 'Unlimited Hits' : `${payment.credits} Credits`);
                    return (
                        <div
                            key={payment.id}
                            className="bg-background border border-border rounded-xl p-4 space-y-3 shadow-xs"
                        >
                            <div className="flex items-center justify-between">
                                <div className="font-bold text-foreground text-base">
                                    {planDisplay}
                                </div>
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                    PAID
                                </span>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-xs py-2 border-y border-border/50">
                                <div>
                                    <span className="text-muted-foreground block">Date</span>
                                    <span className="font-medium text-foreground">
                                        {new Date(payment.date).toLocaleDateString()}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-muted-foreground block">Amount</span>
                                    <span className="font-bold text-foreground font-mono">
                                        {payment.currency} {payment.amount.toFixed(2)}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-muted-foreground block">Validity</span>
                                    <span className="font-medium text-foreground">
                                        {payment.expiryDate ? new Date(payment.expiryDate).toLocaleDateString() : 'Lifetime'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-muted-foreground block">Ref ID</span>
                                    <span className="font-mono text-[10px] text-muted-foreground truncate block">
                                        {payment.razorpay_payment_id || payment.receipt || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-1">
                                <button
                                    onClick={() => downloadInvoice(payment)}
                                    className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-semibold bg-primary text-primary-foreground rounded-lg transition-all active:scale-95 shadow-sm"
                                >
                                    <Download className="h-3.5 w-3.5" />
                                    Download PDF Invoice
                                </button>
                                {onDelete && (
                                    <button
                                        onClick={() => {
                                            if (window.confirm('Are you sure you want to delete this payment record?')) {
                                                onDelete(payment.id);
                                            }
                                        }}
                                        className="ml-2 p-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                                        title="Delete Record"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
